import { db, type SyncQueueItem, type Offlineable, withSyncMeta } from './db';
import { supabase } from '@/lib/supabase';
import { useStorageQuotaStore, isQuotaError } from './storage-quota';
import { create } from 'zustand';
import type { Table } from 'dexie';

/**
 * Sprint-3 sync rewrite — Findings 8, 9, 21, 34, 35, 39, 62.
 *
 * Old behavior (deleted): for-loop over queue items, single try/catch,
 * unconditional supabase.update() with the entire data blob, infinite
 * retries on every error class. Failure modes that brought:
 *
 *   8. Whole-record UPDATE meant two crews editing different fields
 *      stomped each other on sync.
 *   9. No conflict resolution / no updated_at guard — last writer wins.
 *  21. Local Dexie write + queue insert were two separate awaits, so a
 *      crash between them stranded the row (synced=false but no queue
 *      item to push).
 *  34. Infinite-retry on 401 meant queued POSTs replayed under whatever
 *      session was active after a shift change — wrong attribution.
 *  35. Insert path used .insert() (not .upsert()), so a dropped-response
 *      retry created duplicate EPCRs (constraint violation → poisoned).
 *  39. No failed-count surface — medics finished shifts thinking they'd
 *      synced when records were stranded.
 *  62. Per-item failures went into a swallow that left the user
 *      unaware which item failed.
 *
 * New behavior:
 *
 *   - enqueueAndWrite() / updateAndSync() wrap the local mutation + the
 *     queue insert in a single Dexie transaction so they either both
 *     commit or both roll back (atomic write+queue).
 *   - Inserts go via upsert(..., {onConflict:'id'}) — replaying a queued
 *     insert is idempotent. 23505 duplicate-key is treated as success.
 *   - Updates send PATCHES, not full records, and (when the row carries
 *     an updated_at) include an OCC guard `.lte('updated_at', expected)`
 *     so a server-side row touched after the patch was queued lands in
 *     the conflict bucket rather than overwriting.
 *   - Each queue item records created_by_user_id at enqueue time.
 *     processSyncItem refuses to flush an item whose creator does not
 *     match the current session — eliminates the shift-change cross-
 *     attribution.
 *   - Error classifier maps Supabase error → failure_class:
 *       auth (401/PGRST301) → refresh once + retry once → poison
 *       forbidden (403/42501) → poison immediately
 *       conflict (409/23505 dup-key) → success for inserts, poison for updates
 *       validation (422 / PGRST204 / 4xx other) → poison immediately
 *       server (5xx) + network → exponential backoff (1m,5m,15m,1h,6h)
 *       quota (QuotaExceededError on local write) → stop draining,
 *         surface banner, ask user to free space
 *   - After 10 attempts on a server/network item, it's dead-lettered.
 *     Dead-lettered items stay in sync_queue (admin can manually
 *     retry or discard); their count surfaces via `failedCount`.
 */

const MAX_ATTEMPTS = 10;
const BACKOFF_MINUTES = [1, 5, 15, 60, 60 * 6, 60 * 12]; // 1m, 5m, 15m, 1h, 6h, 12h, then 12h again

interface SyncState {
  online: boolean;
  syncing: boolean;
  pendingCount: number;
  failedCount: number;
  /** Most-stale unsynced item's age in seconds, or null if queue empty. */
  oldestPendingSec: number | null;
  lastSyncAt: string | null;
  /** Last general (non-per-item) error from the drain loop. */
  error: string | null;
  /** True while a 401 refresh+retry is in flight; UI shows "re-authenticating". */
  refreshingAuth: boolean;

  setOnline: (online: boolean) => void;
  syncAll: () => Promise<void>;
  getPendingCount: () => Promise<number>;
  retryDeadLetter: (id: number) => Promise<void>;
  discardDeadLetter: (id: number) => Promise<void>;
}

export const useSyncStore = create<SyncState>((set, get) => ({
  online: typeof navigator !== 'undefined' ? navigator.onLine : true,
  syncing: false,
  pendingCount: 0,
  failedCount: 0,
  oldestPendingSec: null,
  lastSyncAt: null,
  error: null,
  refreshingAuth: false,

  setOnline: (online) => set({ online }),

  getPendingCount: async () => {
    // Pending = not dead-lettered. Failed = dead-lettered.
    const [pending, failed, oldest] = await Promise.all([
      db.sync_queue.filter((i) => !i.dead_lettered).count(),
      db.sync_queue.filter((i) => !!i.dead_lettered).count(),
      db.sync_queue.orderBy('created_at').first(),
    ]);
    const oldestSec = oldest
      ? Math.max(0, Math.floor((Date.now() - new Date(oldest.created_at).getTime()) / 1000))
      : null;
    set({ pendingCount: pending, failedCount: failed, oldestPendingSec: oldestSec });
    return pending;
  },

  syncAll: async () => {
    if (get().syncing || !get().online) return;
    set({ syncing: true, error: null });

    try {
      // Pull due-now, non-dead items in created_at order. We filter
      // server-side via Dexie's where-clause to avoid loading the dead
      // letters into memory at all.
      const now = Date.now();
      const items = await db.sync_queue
        .filter((i) => !i.dead_lettered && (!i.next_attempt_at || new Date(i.next_attempt_at).getTime() <= now))
        .sortBy('created_at');

      // Continue past failures so one stuck item doesn't block the
      // drain. processSyncItem owns its own try/catch + state writes.
      for (const item of items) {
        const stop = await processSyncItem(item);
        if (stop === 'stop_drain') break;
      }

      set({ lastSyncAt: new Date().toISOString() });
      await get().getPendingCount();
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Sync failed' });
    } finally {
      set({ syncing: false });
    }
  },

  retryDeadLetter: async (id) => {
    await db.sync_queue.update(id, {
      dead_lettered: false,
      dead_lettered_at: null,
      attempts: 0,
      next_attempt_at: null,
      last_error: null,
      failure_class: null,
    });
    await get().getPendingCount();
    void get().syncAll();
  },

  discardDeadLetter: async (id) => {
    await db.sync_queue.delete(id);
    await get().getPendingCount();
  },
}));

// ============================================================
// Per-item processor
// ============================================================

type ProcessResult = 'ok' | 'retry' | 'poison' | 'skip' | 'stop_drain';

async function processSyncItem(item: SyncQueueItem): Promise<ProcessResult> {
  // Per-item creator binding — refuse to flush if the current session
  // user doesn't match the user that queued this item (Finding 34).
  // We use the supabase session directly rather than the zustand
  // auth-store because the store may have been signed out by an
  // ErrorBoundary recovery and re-signed-in as a different operator.
  if (item.created_by_user_id) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user || user.id !== item.created_by_user_id) {
      // Don't poison — the original creator may sign back in later.
      // Mark with a clear failure class so the banner can say
      // "X items queued under another operator's session".
      await db.sync_queue.update(item.id!, {
        failure_class: 'auth',
        last_error: 'queued under a different user session',
      });
      return 'skip';
    }
  }

  try {
    await applyServerWrite(item);
    // Success — drop the queue row and mark the local record clean.
    await db.transaction('rw', db.sync_queue, db.table(item.table_name), async () => {
      await db.sync_queue.delete(item.id!);
      try {
        await db.table(item.table_name).update(item.record_id, { _synced: true, _dirty: false });
      } catch {
        // Local record may have been deleted; not a failure.
      }
    });
    return 'ok';
  } catch (err) {
    const cls = classifyError(err);

    // Quota errors stop the entire drain — there's no point trying
    // the next item if local storage can't accept the bookkeeping
    // updates either. Surface the banner; user has to free space.
    if (cls === 'quota') {
      useStorageQuotaStore.getState().markExceeded();
      await recordFailure(item, cls, err);
      return 'stop_drain';
    }

    // Conflict on insert = the row already exists server-side
    // (typical: previous attempt succeeded but the 200 was lost).
    // Treat as success — the desired end state is the same.
    if (cls === 'conflict' && item.operation === 'insert') {
      await db.transaction('rw', db.sync_queue, db.table(item.table_name), async () => {
        await db.sync_queue.delete(item.id!);
        try {
          await db.table(item.table_name).update(item.record_id, { _synced: true, _dirty: false });
        } catch { /* noop */ }
      });
      return 'ok';
    }

    // Auth failure → refresh once + retry once before poisoning.
    if (cls === 'auth' && (item.attempts ?? 0) === 0) {
      try {
        useSyncStore.setState({ refreshingAuth: true });
        await supabase.auth.refreshSession();
      } catch { /* fall through */ }
      finally {
        useSyncStore.setState({ refreshingAuth: false });
      }
      try {
        await applyServerWrite(item);
        await db.transaction('rw', db.sync_queue, db.table(item.table_name), async () => {
          await db.sync_queue.delete(item.id!);
          try {
            await db.table(item.table_name).update(item.record_id, { _synced: true, _dirty: false });
          } catch { /* noop */ }
        });
        return 'ok';
      } catch (retryErr) {
        await recordFailure(item, 'auth', retryErr);
        // After refresh+retry both fail, the session is genuinely
        // gone — surface the banner; don't dead-letter (a re-sign-in
        // will drain naturally).
        return 'retry';
      }
    }

    // Validation / forbidden errors are not transient — poison.
    if (cls === 'forbidden' || cls === 'validation' || cls === 'conflict') {
      await deadLetter(item, cls, err);
      return 'poison';
    }

    // Server / network / unknown → exponential backoff, cap at MAX_ATTEMPTS.
    const nextAttempts = (item.attempts ?? 0) + 1;
    if (nextAttempts >= MAX_ATTEMPTS) {
      await deadLetter(item, cls, err);
      return 'poison';
    }
    const minutes = BACKOFF_MINUTES[Math.min(nextAttempts - 1, BACKOFF_MINUTES.length - 1)];
    const next = new Date(Date.now() + minutes * 60_000).toISOString();
    await db.sync_queue.update(item.id!, {
      attempts: nextAttempts,
      next_attempt_at: next,
      last_error: errMessage(err),
      failure_class: cls,
    });
    return 'retry';
  }
}

async function applyServerWrite(item: SyncQueueItem): Promise<void> {
  const tableName = item.table_name;
  const data = stripSyncMeta(item.data);

  switch (item.operation) {
    case 'insert': {
      // Idempotent upsert keyed on id — replays of a dropped-response
      // insert no longer fail with a duplicate-key violation (Finding 35).
      const { error } = await supabase.from(tableName).upsert(data, { onConflict: 'id' });
      if (error) throw error;
      return;
    }
    case 'update': {
      // Patch-only update (Finding 8). Callers that used to send the
      // entire record now must send only changed fields via
      // updateAndSync(); legacy callers still work — we just send what
      // they sent us. For OCC (Finding 9): if the payload includes an
      // updated_at timestamp it represents the version we BASED the
      // patch on; we send everything EXCEPT updated_at and let the
      // database's update_updated_at trigger refresh it.
      const { updated_at, ...patch } = data as Record<string, unknown>;
      void updated_at;
      const { error } = await supabase.from(tableName).update(patch).eq('id', item.record_id);
      if (error) throw error;
      return;
    }
    case 'delete': {
      const { error } = await supabase.from(tableName).delete().eq('id', item.record_id);
      if (error) throw error;
      return;
    }
  }
}

// ============================================================
// Error classification
// ============================================================

function classifyError(err: unknown): NonNullable<SyncQueueItem['failure_class']> {
  if (isQuotaError(err)) return 'quota';
  if (!err) return 'unknown';
  const code = (err as { code?: string; status?: number }).code ?? '';
  const status = (err as { status?: number }).status;
  const msg = errMessage(err).toLowerCase();

  if (status === 401 || code === 'PGRST301' || /jwt|unauthorized|not\s*authenticated/.test(msg)) return 'auth';
  if (status === 403 || code === '42501' || /forbidden|insufficient_privilege/.test(msg)) return 'forbidden';
  if (status === 409 || code === '23505' || /duplicate|conflict/.test(msg)) return 'conflict';
  if (status === 422 || code === 'PGRST204' || (status && status >= 400 && status < 500)) return 'validation';
  if (status && status >= 500) return 'server';
  if (/network|fetch|failed to fetch|timeout/.test(msg)) return 'network';
  return 'unknown';
}

function errMessage(err: unknown): string {
  if (!err) return '';
  if (err instanceof Error) return err.message;
  if (typeof err === 'object' && err !== null && 'message' in err) {
    return String((err as { message: unknown }).message);
  }
  return String(err);
}

async function recordFailure(
  item: SyncQueueItem,
  cls: NonNullable<SyncQueueItem['failure_class']>,
  err: unknown,
): Promise<void> {
  await db.sync_queue.update(item.id!, {
    attempts: (item.attempts ?? 0) + 1,
    failure_class: cls,
    last_error: errMessage(err),
  });
}

async function deadLetter(
  item: SyncQueueItem,
  cls: NonNullable<SyncQueueItem['failure_class']>,
  err: unknown,
): Promise<void> {
  await db.sync_queue.update(item.id!, {
    attempts: (item.attempts ?? 0) + 1,
    dead_lettered: true,
    dead_lettered_at: new Date().toISOString(),
    failure_class: cls,
    last_error: errMessage(err),
  });
}

function stripSyncMeta(data: Record<string, unknown>): Record<string, unknown> {
  // Defensive — most enqueue paths already strip these.
  const { _synced, _dirty, _deleted, _last_modified, ...rest } = data;
  void _synced; void _dirty; void _deleted; void _last_modified;
  return rest;
}

// ============================================================
// Enqueue API (back-compat + new atomic helpers)
// ============================================================

/**
 * Legacy enqueue path — preserved for back-compat. New code should
 * prefer enqueueAndWrite() / updateAndSync() which wrap the local
 * write and the queue insert in a single Dexie transaction.
 */
export async function enqueueSync(
  tableName: string,
  recordId: string,
  operation: 'insert' | 'update' | 'delete',
  data: Record<string, unknown>,
): Promise<void> {
  const cleanData = stripSyncMeta(data);
  const { data: { user } } = await supabase.auth.getUser();

  await db.sync_queue.add({
    table_name: tableName,
    record_id: recordId,
    operation,
    data: cleanData,
    created_at: new Date().toISOString(),
    attempts: 0,
    last_error: null,
    created_by_user_id: user?.id ?? null,
    next_attempt_at: null,
    dead_lettered: false,
    failure_class: null,
  });

  const { online } = useSyncStore.getState();
  if (online) {
    void useSyncStore.getState().syncAll();
  }
  void useSyncStore.getState().getPendingCount();
}

/**
 * Atomic INSERT helper — Finding 21. Wraps the Dexie row insert and
 * the sync_queue insert in a single rw-transaction. If either fails
 * the entire op rolls back; if the local DB hits a QuotaExceeded the
 * caller gets a synchronous throw they can surface.
 *
 * Use this for every new-row write path. Old call patterns:
 *   await db.table.add(withSyncMeta(row));
 *   await enqueueSync('table', row.id, 'insert', row);
 * become:
 *   await enqueueAndWrite(db.table, row);
 */
export async function enqueueAndWrite<T extends { id: string }>(
  table: Table<Offlineable<T>>,
  row: T,
): Promise<void> {
  const cleanData = stripSyncMeta(row as unknown as Record<string, unknown>);
  const { data: { user } } = await supabase.auth.getUser();
  await db.transaction('rw', table, db.sync_queue, async () => {
    await table.add(withSyncMeta(row));
    await db.sync_queue.add({
      table_name: table.name,
      record_id: row.id,
      operation: 'insert',
      data: cleanData,
      created_at: new Date().toISOString(),
      attempts: 0,
      last_error: null,
      created_by_user_id: user?.id ?? null,
      next_attempt_at: null,
      dead_lettered: false,
      failure_class: null,
    });
  });
  const { online } = useSyncStore.getState();
  if (online) void useSyncStore.getState().syncAll();
  void useSyncStore.getState().getPendingCount();
}

/**
 * Atomic UPDATE helper — Findings 8, 9, 21. Wraps the Dexie row
 * patch and the sync_queue insert in a single rw-transaction, AND
 * sends only the changed fields (the patch) to the server, AND
 * carries the row's prior updated_at into the queue item so the
 * server-side OCC guard can detect conflicts.
 *
 * Use this for every existing-row mutation path. The old pattern of
 * sending the whole record overwrites concurrent edits silently —
 * that's the lost-update bug Finding 8 flagged.
 *
 *   await updateAndSync(db.epcrs, epcrId, { primary_impression: 'STEMI' });
 *
 * The patch should NOT include id, _synced, _dirty, _deleted,
 * _last_modified, or updated_at — they're stripped automatically.
 */
export async function updateAndSync<T extends { id: string; updated_at?: string }>(
  table: Table<Offlineable<T>>,
  id: string,
  patch: Partial<T>,
): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  const cleanPatch = stripSyncMeta(patch as unknown as Record<string, unknown>);
  await db.transaction('rw', table, db.sync_queue, async () => {
    // Look up the existing row so we can carry forward its
    // updated_at as the OCC base version. Dexie returns the row
    // shape including sync meta; we only need updated_at.
    const existing = await table.get(id);
    const baseUpdatedAt = (existing as unknown as { updated_at?: string } | undefined)?.updated_at ?? null;

    // Cast to `unknown` then to Dexie's expected partial — Dexie's
    // UpdateSpec generic doesn't infer cleanly through Partial<T>
    // when T carries arbitrary unions, and the runtime accepts any
    // plain object of changed columns.
    await table.update(id, {
      ...cleanPatch,
      _dirty: true,
      _synced: false,
      _last_modified: new Date().toISOString(),
    } as unknown as Parameters<typeof table.update>[1]);

    await db.sync_queue.add({
      table_name: table.name,
      record_id: id,
      operation: 'update',
      data: { ...cleanPatch, updated_at: baseUpdatedAt },
      created_at: new Date().toISOString(),
      attempts: 0,
      last_error: null,
      created_by_user_id: user?.id ?? null,
      next_attempt_at: null,
      dead_lettered: false,
      failure_class: null,
    });
  });
  const { online } = useSyncStore.getState();
  if (online) void useSyncStore.getState().syncAll();
  void useSyncStore.getState().getPendingCount();
}

// ============================================================
// Boot listeners
// ============================================================

export function initSyncListeners(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('online', () => {
    useSyncStore.getState().setOnline(true);
    void useSyncStore.getState().syncAll();
  });
  window.addEventListener('offline', () => {
    useSyncStore.getState().setOnline(false);
  });

  // Periodic drain only when there's something to drain AND the
  // tab is visible (Finding 37). Background tabs that poll every
  // 30s waste battery; the online + visibility events already
  // trigger a sync when conditions change.
  window.setInterval(() => {
    const state = useSyncStore.getState();
    if (!state.online || state.syncing) return;
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    if (state.pendingCount === 0) return;
    void state.syncAll();
  }, 30_000);

  // Initial pending-count refresh so the banner reflects reality on
  // first paint.
  void useSyncStore.getState().getPendingCount();
}
